/**
 * 居民主页的主体「TA 的星球生活」（UI-WORLD-LIVING-117-3，参考稿 05-life-A；I 评审 UI-WORLD-AWARD-REVIEW-116 D 节）：
 * 首屏 TA 的家 → 名字和 TA 自己的一句话 → TA 的一天 → TA 最近见过的 → TA 的照片 → TA 的梦想；「把 TA 带回家」在页末（页面给）。
 * 「TA 最近见过的」不叫「TA 的朋友」：相遇读口只保证两位互相认识（共同经历留下的、两个方向都还在的认识关系），不保证结交（I 10-16）。
 * 只写后台给的公开事实（挑法见 ./lifeModel），没有的那一节整节不出，不写「暂无」占位；性格照原话写成一行字，不拆胶囊。
 * 不写技术话：时间线读不到（失败、名单外）就只说此刻。
 * TA 的图：在家、有官方形象时叠在首屏屋里；叠不上（出门了、没有官方形象、打不开）就在名字旁放头像，退回链照原来的居民主页（官方形象 → 原来的照片 → 中性底）。
 * 访客取图的闸照原来的居民主页：主页公开、回包就是这一位，才给访客对象；不然不取（家人可见、跟随可见的主页不起访客取图）。
 * 首屏那张打不开：撤掉，名字旁放头像，从「原来的照片」那一级开始（同一张打不开的官方形象不再试一遍）；底图打不开整块首屏不出，也改放头像。
 * 访客那条路还在取首屏那张时，名字旁先不放头像（评审 R1 高：取到以后整段往上跳约 98px）。不是星球居民的公开主页（resident 为空）不放首屏：不知道 TA 住在哪。
 * 长文字（TA 的话、性格、梦想、小事）照原来的居民主页：超过 120 字先露 5 行，按钮展开／收起；全文一直在页面里（读屏照读全文）。
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { Link, useLocation } from "react-router";
import type { PublicFeedPhoto, PublicPetView } from "@/shared/contracts";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { serverNow } from "@/shared/time/clock";
import { Icon, PlanetEventLine } from "@/shared/ui";
import { ResidentPortrait } from "../ResidentPortrait";
import { ResidentHome } from "../ResidentHome";
import { isOriginalCharacter, nowLine, ORIGINAL_CHARACTER_TAG, speciesName } from "../residentView";
import { identityFaceCrop } from "../world/guestStoriesModel";
import { LifeScene, useSceneFigure } from "./LifeScene";
import { dayLabel, lifeDay, lifeFriends, lifePhotos, metDay, planetClock, type LifeFriend } from "./lifeModel";
import { useResidentTimeline } from "./useResidentTimeline";
import { SameNameTag } from "@/shared/ui/SameNameTag";
import { sameNameTags } from "@/shared/ui/sameNameTags";
import "./resident-life.css";

/** 照片那一节先出几张 */
const PHOTOS_FIRST = 6;

export function ResidentLife({ view, guest, petId }: { view: PublicPetView; guest: boolean; petId: string }) {
  const { profile, resident, posts } = view;
  const name = profile.display_name;
  const personality = resident?.personality?.trim() || null;
  const bio = profile.bio?.trim() || null;
  // TA 自己的一句话：bio 原话；没有 bio、或和性格一样时说性格那一句（不改写、不加「TA 说」）
  const words = bio ?? personality;
  const nowMs = serverNow();
  const timeline = useResidentTimeline(petId || null, guest);
  const items = timeline.data?.state === "known" ? timeline.data.items : [];
  const day = lifeDay({ posts, items, petId, nowMs });
  // 打不开的图记下来：那一张不出（TA 的一天里那条照样说，只是没图）；照片那一节按剩下的算，一张都不剩整节不出
  const [broken, setBroken] = useState<ReadonlySet<string>>(() => new Set());
  const onBroken = (url: string) => setBroken((bad) => (bad.has(url) ? bad : new Set(bad).add(url)));
  const shownPhotos = new Set(day.entries.flatMap((entry) => (entry.photo ? [entry.photo.url] : [])));
  const photos = lifePhotos(items, petId).filter((photo) => !shownPhotos.has(photo.url) && !broken.has(photo.url));
  const friends = useFriendViews(lifeFriends(items, petId), guest);
  // 同一屏同名时居民那一边加小字（SAME-NAME-LABEL-117，属主 6c2b 10-16 授权）：这一页的 TA 本人也算在这一屏里；
  // 有居民那一栏的才是居民，没有的不猜（null）；主人的宠物不加、不露城市
  const sameName = sameNameTags([
    { id: petId, name, resident: resident ? true : null, city: resident?.city },
    ...friends.flatMap(({ friend, view: other }) => (other ? [{ id: friend.petId, name: other.profile.display_name, resident: other.resident ? true : null, city: other.resident?.city }] : [])),
  ]);
  const asleep = resident?.asleep === true;
  const now = resident ? nowLine(resident) : null;
  // 来历（诚实标识，例如「PetSoul 原创伙伴」）：居民那一栏的 source_note，没有用主页的 origin_label；都没有不写
  const source = resident?.source_note?.trim() || profile.origin_label?.trim() || null;
  // 头像退回链（照原来的居民主页）：官方形象只认居民那一栏的地址；访客只取这一位自己的公开形象（主页公开、回包是这一位）
  const photo = profile.avatar_url ?? resident?.avatar_url ?? null;
  const official = resident?.official_avatar === true && photo === (resident?.avatar_url ?? null);
  const guestSubject = guest && profile.visibility === "public" && profile.pet_id === petId && (!resident || resident.pet_id === petId)
    ? { petId, identity: resident?.identity ?? profile.identity } : null;
  const scene = useSceneFigure(resident, official, guest, guestSubject);
  const [figureFailed, setFigureFailed] = useState<string | null>(null);
  const [plateFailed, setPlateFailed] = useState(false);
  const figure = scene.src && scene.src !== figureFailed ? scene.src : null;
  // 首屏：TA 出门了（空屋子写一句不在家）、或在家且叠得上（含还在取）才放；在家却叠不上不放（评审 R2：不留读屏说「在家」却空着的屋子）
  const sceneOf = resident && !plateFailed && (resident.presence !== "at_home" || Boolean(figure) || scene.pending) ? resident : null;
  const figureBroken = Boolean(scene.src && scene.src === figureFailed);
  // 名字旁的头像：首屏在、而且 TA 叠上了（或还在取）才不放；首屏撤了（底图打不开、在家却叠不上）就放
  const showPortrait = !(sceneOf && (figure || scene.pending));
  const [allPhotos, setAllPhotos] = useState(false);
  // 「看看全部」点了以后按钮没了：焦点落到新露出来的第一张（第 7 张），不掉到页面最上面（评审 R2）
  const photoListRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    if (allPhotos) photoListRef.current?.querySelectorAll<HTMLButtonElement>(".ps-life-photo__open")[PHOTOS_FIRST]?.focus();
  }, [allPhotos]);
  return <div className="ps-life">
    {sceneOf ? <LifeScene resident={sceneOf} name={name} figure={figure} onFigureError={setFigureFailed} onPlateError={() => setPlateFailed(true)} /> : null}
    <header className={`ps-life-intro${sceneOf ? "" : " is-plain"}`}>
      <div className="ps-life-intro__head">
      {showPortrait ? <div className="ps-life-intro__portrait">
        {figureBroken ? <ResidentPortrait name={name} species={profile.species} photoUrl={resident?.fallback_avatar_url ?? null} official={false}
          guestMedia={guest ? null : undefined} origin={resident?.origin ?? null} size={88} surface="paper" />
          : <ResidentPortrait focus={resident && official ? identityFaceCrop(resident) : null} name={name} species={profile.species} photoUrl={photo}
          fallbackUrl={resident?.fallback_avatar_url ?? null}
          official={guest ? official || (!resident && profile.identity?.source_kind === "platform_official" && photo === profile.identity.url) : official}
          guestMedia={guest ? guestSubject : undefined} origin={resident?.origin ?? null} size={88} surface="paper" />}
      </div> : null}
      <div className="ps-life-intro__title">
        <p className="ps-life-intro__kicker" aria-hidden="true">TA 的星球生活</p>
        <div className="ps-life-intro__name">
          <h1 id="resident-title">{name}<SameNameTag tag={sameName.get(petId)} /></h1>
          {/* 原创角色的标照旧在名字旁（D5，诚实标识不挪远） */}
          {isOriginalCharacter(resident) ? <span className="ps-life-intro__tag" data-resident-kind="public_npc">{ORIGINAL_CHARACTER_TAG}</span> : null}
        </div>
      </div>
      </div>
      <p className="ps-life-intro__facts">
        {speciesName(profile.species)}
        {resident ? <>{" · "}住在<ResidentHome resident={resident} /></> : null}
      </p>
      {source ? <p className="ps-life-intro__source">来自：{source}</p> : null}
      {words ? <LifeText text={words} label={bio && bio !== personality ? "这段故事" : "这段性格介绍"} className="ps-life-intro__words" /> : null}
      {personality && words !== personality ? <LifeText text={personality} label="这段性格介绍" className="ps-life-intro__trait" prefix="性格：" /> : null}
    </header>
    {now || day.entries.length ? <section className="ps-life-section ps-life-day" aria-labelledby="life-day">
      <div className="ps-life-section__head"><h2 id="life-day">TA 的一天</h2></div>
      <ol className="ps-life-day__list">
        {/* 不是今天：日子写在那几条前面，此刻前面写「今天」，两段分得清（评审 R1） */}
        {day.day && day.entries.length ? <li className="ps-life-day__date">{dayLabel(day.day)}</li> : null}
        {day.entries.map((entry) => <li key={entry.key} className="ps-life-day__entry" data-kind={entry.kind}>
          <time className="ps-life-day__time" dateTime={entry.at}>{planetClock(entry.ms)}</time>
          <div className="ps-life-day__body">
            <LifeText text={entry.text} label="这条小事" />
            <PlanetEventLine event={entry.event} />
            {entry.photo && !broken.has(entry.photo.url) ? <LifePhoto photo={entry.photo} name={name} variant="day" onBroken={onBroken} /> : null}
          </div>
        </li>)}
        {/* 此刻排在最后：一天按时刻往下走，走到现在 */}
        {now && day.day && day.entries.length ? <li className="ps-life-day__date">今天</li> : null}
        {now ? <li className={`ps-life-day__entry is-now${asleep ? " is-asleep" : ""}`}>
          <span className="ps-life-day__time">此刻</span>
          <div className="ps-life-day__body"><p>{asleep && !/睡/.test(now) ? `睡着了，${now}` : now}</p></div>
        </li> : null}
      </ol>
    </section> : null}
    {friends.some((f) => f.view || f.pending) ? <section className="ps-life-section ps-life-friends" aria-labelledby="life-friends">
      <div className="ps-life-section__head"><h2 id="life-friends">TA 最近见过的</h2></div>
      <ul className="ps-life-friends__list" aria-busy={friends.some((f) => f.pending) || undefined}>
        {friends.map(({ friend, view, pending }) => view ? <LifeFriendItem key={friend.petId} friend={friend} view={view} guest={guest} label={metDay(friend.at, nowMs)} tag={sameName.get(friend.petId)} />
          : pending ? <li key={friend.petId} className="ps-life-friend is-pending" aria-hidden="true" /> : null)}
      </ul>
    </section> : null}
    {photos.length ? <section className="ps-life-section ps-life-photos" aria-labelledby="life-photos">
      <div className="ps-life-section__head"><h2 id="life-photos">TA 的照片</h2></div>
      <ul ref={photoListRef} className="ps-life-photos__grid">
        {(allPhotos ? photos : photos.slice(0, PHOTOS_FIRST)).map((photo) => <li key={photo.url}><LifePhoto photo={photo} name={name} variant="grid" onBroken={onBroken} /></li>)}
      </ul>
      {!allPhotos && photos.length > PHOTOS_FIRST ? <button type="button" className="ps-life-photos__more" onClick={() => setAllPhotos(true)}>看看全部 {photos.length} 张</button> : null}
    </section> : null}
    {resident?.dream?.trim() ? <section className="ps-life-section ps-life-dream" aria-labelledby="life-dream">
      <div className="ps-life-section__head"><h2 id="life-dream">TA 的梦想</h2></div>
      <LifeText text={resident.dream.trim()} label="TA 的梦想" className="ps-life-dream__text" />
    </section> : null}
  </div>;
}

/**
 * TA 最近见过的那几位：读对方自己的公开主页（访客走访客那条路，和点进去看到的同一份缓存），拿名字和形象。
 * 读到了才出那一位（没公开了、出错的撤掉，不写代称）；还在读的先占位（不一位一位冒出来把下面顶下去，评审 R1）；
 * 都读完、一位都没读到整节不出，不留一个空标题。
 */
function useFriendViews(friends: readonly LifeFriend[], guest: boolean): { friend: LifeFriend; view: PublicPetView | null; pending: boolean }[] {
  const { pets } = useServices();
  const readable = !guest || typeof pets.publicPetGuest === "function";
  const views = useQueries({
    queries: friends.map((friend) => ({
      queryKey: guest ? queryKeys.publicPetGuest(friend.petId) : queryKeys.publicPet(friend.petId),
      queryFn: () => (guest ? pets.publicPetGuest!(friend.petId) : pets.publicPet(friend.petId)),
      enabled: readable,
      staleTime: 30_000,
      retry: false,
    })),
  });
  return friends.map((friend, index) => {
    const view = views[index];
    const ok = Boolean(view?.isSuccess && view.data.profile.pet_id === friend.petId);
    return { friend, view: ok ? view!.data! : null, pending: readable && Boolean(view?.isPending) };
  });
}

/**
 * 一段原话：超过 120 字先露 5 行，按钮展开／收起（照原来居民主页的 ProfileText）；全文一直在 DOM 里，读屏照读全文。
 * prefix 是一个看得见的小标签（例如「性格：」），后面照旧是原话。长的段落用 pretty 断行（balance 只管 6 行以内，展开后会剩孤字）。
 */
function LifeText({ text, label, className = "", prefix }: { text: string; label: string; className?: string; prefix?: string }) {
  const id = useId();
  const [expanded, setExpanded] = useState(false);
  const long = text.length > 120;
  return <div className={`ps-life-text ${className}`.trim()}>
    <p id={id} className={long ? (expanded ? "is-long" : "is-long is-collapsed") : undefined}>{prefix ? <span className="ps-life-text__label">{prefix}</span> : null}{text}</p>
    {long ? <button type="button" className="ps-life-text__expand" aria-expanded={expanded} aria-controls={id} onClick={() => setExpanded((value) => !value)}>
      {expanded ? "收起" : "展开"}<span className={/^[A-Za-z]/.test(label) ? "ps-life-text__what is-latin" : "ps-life-text__what"}>{label}</span><Icon name="chevron" size={16} />
    </button> : null}
  </div>;
}

/**
 * 一位：整块是去对方主页的链接，名字下写哪天见过（「今天」「10 月 15 日」；节标题已经说了见过，相遇那句原文在 TA 的一天里，评审 R1、R2）。
 * 头像只给看（读屏名不再说一遍「X 的官方形象」），链接读「名字，日子」。
 * 从这里点进对方主页，对方主页的「返回」回到这一位（带 backTo），不回星球（评审 R1）。
 */
function LifeFriendItem({ friend, view, guest, label, tag }: { friend: LifeFriend; view: PublicPetView; guest: boolean; label: string; tag?: string }) {
  const location = useLocation();
  const { profile, resident } = view;
  // 头像退回链和取图路子照居民主页自己的（ResidentPage 的 ResidentProfile）：官方形象只认居民那一栏的地址；访客只取这一位自己的公开形象
  const photo = profile.avatar_url ?? resident?.avatar_url ?? null;
  const official = resident?.official_avatar === true && photo === (resident?.avatar_url ?? null);
  const guestMedia = profile.visibility === "public" && (!resident || resident.pet_id === friend.petId) ? { petId: friend.petId, identity: resident?.identity ?? profile.identity } : null;
  return <li className="ps-life-friend">
    <Link to={`/world/residents/${encodeURIComponent(friend.petId)}${guest ? "?view=guest" : ""}`} state={{ backTo: `${location.pathname}${location.search}` }} className="ps-life-friend__link">
      <span className="ps-life-friend__face" aria-hidden="true"><ResidentPortrait focus={resident && official ? identityFaceCrop(resident) : null} name={profile.display_name} species={profile.species}
        photoUrl={photo} fallbackUrl={resident?.fallback_avatar_url ?? null} official={official}
        guestMedia={guest ? guestMedia : undefined} origin={resident?.origin ?? null} size={72} surface="paper" /></span>
      <span className="ps-life-friend__name">{profile.display_name}<SameNameTag tag={tag} /></span>
      {label ? <span className="ps-life-friend__met"><span className="visually-hidden">，</span>{label}</span> : null}
    </Link>
  </li>;
}

/**
 * 一张真实生活照：小图（TA 的一天里是宽幅，照片宫格里是 4:3），点了放大看整张（模态，Esc、关上都能关，焦点回到这张小图）。
 * 打不开就告诉主体（onBroken），由主体把这一张撤掉（不留破图）。
 */
function LifePhoto({ photo, name, variant, onBroken }: { photo: PublicFeedPhoto; name: string; variant: "day" | "grid"; onBroken: (url: string) => void }) {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog) return;
    const trigger = buttonRef.current;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (typeof dialog.showModal === "function") dialog.showModal();
    return () => {
      const heldFocus = dialog.contains(document.activeElement) || document.activeElement === document.body;
      if (dialog.open) dialog.close();
      document.body.style.overflow = overflow;
      if (heldFocus) queueMicrotask(() => { if (trigger?.isConnected) trigger.focus({ preventScroll: true }); });
    };
  }, [open]);
  return <figure className={`ps-life-photo is-${variant}`}>
    <img src={photo.url} alt={photo.alt} loading="lazy" draggable={false} onError={() => { setOpen(false); onBroken(photo.url); }} />
    <button ref={buttonRef} type="button" className="ps-life-photo__open" aria-label={`放大查看${name}的生活照片：${photo.alt}`} onClick={() => setOpen(true)} />
    {open ? <dialog ref={dialogRef} className="ps-life-photo__preview" aria-label={`${name}的完整生活照片`} onCancel={(event) => { event.preventDefault(); setOpen(false); }}>
      <div className="ps-life-photo__bar"><span>{name}的生活片段</span><button type="button" autoFocus aria-label="关上照片" onClick={() => setOpen(false)}>×</button></div>
      <img src={photo.url} alt={photo.alt} draggable={false} />
      <p>{photo.alt}</p>
    </dialog> : null}
  </figure>;
}
